import { Table } from "@devexpress/dx-react-grid-bootstrap4";
import React, { useCallback, useMemo, useState } from "react";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { useHistory, useRouteMatch } from "react-router";
import SweetAlert from "sweetalert2";
import { v4 as uuidv4 } from "uuid";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { usePropertyLocales } from "../../components/property-locale-provider";
import { TaxationAPIPromiseClient } from "../../gen/witbooking/backoffice/taxation/v1/taxation_api_grpc_web_pb";
import {
  CreateTaxationRequest,
  DeleteTaxationRequest,
  FindTaxationsRequest,
  GetTaxationRequest,
  Taxation,
  TaxationWrapper,
  UpdateTaxationRequest,
} from "../../gen/witbooking/backoffice/taxation/v1/taxation_api_pb";
import actions from "../../lib/actions";
import { getLanguageFromFullLocale } from "../../lib/config";
import { useGrpcClient, usePropertyGrpcCall } from "../../lib/grpc";
import { useApiClient } from "../../lib/http";
import { regenPropertyCache } from "../common/api";
import { customerMessages } from "../loyaltyCustomer/messages";
import {
  DefaultTableRowActionsProps,
  Delete,
  Edit,
  StyledReactTooltip,
} from "../shared/actions/tableActions";
import { FormConfiguration } from "../shared/crud/form";
import { TableColumns } from "../shared/crud/list";
import SimpleCrud from "../shared/crud/simple-crud";
import { RendererProps } from "../shared/renderers";
import * as TableRenderers from "../shared/renderers/table";
import { mapRenderer, StringRenderer } from "../shared/renderers/table";
import { DateRangePicker } from "../shared/widgets/dateRangePicker";
import { HtmlEditorWidget } from "../shared/widgets/html";
import SectionUnderline from "../shared/widgets/sectionUnderline";
import { SwitchGroup } from "../shared/widgets/SwitchGroup";
import { TaxationTypologyManagerFee } from "../shared/widgets/taxationTypology";
import { translationFieldConfig } from "../shared/widgets/translation";
import SwitchComponent from "../witadmin/CRUD/Common/SwitchGroup";
import {
  CustomTaxation,
  messages,
  messagesFees,
  SwitchWrapper,
  taxationFromObject,
  ThresholdRenderer,
  transformTaxation,
  TYPE,
  TypologyRenderer,
} from "./index";
import { modifiedSchema } from "./modifiedSchema";

function FeesCrudComponent({
  propertyTicker,
  section,
}: {
  propertyTicker: string;
  section: string;
}) {
  const client = useGrpcClient(TaxationAPIPromiseClient);
  const allowedLocales = usePropertyLocales();
  const findTaxations = usePropertyGrpcCall(client, client.findTaxations);
  const updateTaxation = usePropertyGrpcCall(client, client.updateTaxation);
  const getTaxation = usePropertyGrpcCall(client, client.getTaxation);
  const createTaxation = usePropertyGrpcCall(client, client.createTaxation);
  const deleteTaxation = usePropertyGrpcCall(client, client.deleteTaxation);

  const intl = useIntl();
  const apiClient = useApiClient();
  const match = useRouteMatch();
  const history = useHistory();

  const [latestEntity] = useState<CustomTaxation | undefined>();

  const onGetAllFee = useCallback(async () => {
    const request = new FindTaxationsRequest();
    const response = await findTaxations(request);
    const feeList = response.getFeesList().map(m => m.toObject());
    return feeList.map(obj => transformTaxation(obj, TYPE.FEE, true));
  }, [findTaxations]);

  const onGetOneFee = useCallback(
    async (id: string) => {
      const request = new GetTaxationRequest();
      request.setId(id);
      const response = await getTaxation(request);
      const fee = response.getTaxation()?.getFee()?.toObject() as Taxation.AsObject;
      return transformTaxation(fee, TYPE.FEE, true);
    },
    [getTaxation]
  );

  const onUpdateFee = useCallback(
    async (entity: CustomTaxation) => {
      const tax = taxationFromObject(entity, TYPE.FEE);
      const wrapper = new TaxationWrapper();
      wrapper.setFee(tax);
      const request = new UpdateTaxationRequest();
      request.setTaxation(wrapper);
      await updateTaxation(request);
      await regenPropertyCache(apiClient, propertyTicker);
    },
    [apiClient, propertyTicker, updateTaxation]
  );

  const onCreateFee: (entity: CustomTaxation) => void = useCallback(
    async (entity: CustomTaxation) => {
      const tax = taxationFromObject(entity, TYPE.FEE);
      const wrapper = new TaxationWrapper();
      wrapper.setFee(tax);
      const request = new CreateTaxationRequest();
      request.setTaxation(wrapper);
      await createTaxation(request);
      await regenPropertyCache(apiClient, propertyTicker);
    },
    [apiClient, createTaxation, propertyTicker]
  );

  const showMessageDeleted = useCallback(
    async (ok: boolean = true) => {
      await SweetAlert.fire({
        title: ok
          ? intl.formatMessage(messages.deleteSuccess)
          : intl.formatMessage(messages.deleteError),
        icon: ok ? "success" : "warning",
      });
    },
    [intl]
  );

  const onDeleteFee = useCallback(
    async (id: string) => {
      const request = new DeleteTaxationRequest();
      request.setId(id);
      try {
        await deleteTaxation(request);
        await regenPropertyCache(apiClient, propertyTicker);
        await showMessageDeleted();
      } catch (error) {
        console.error(`Error request ${request}. error: ${error}`);
        await showMessageDeleted(false);
      }
    },
    [apiClient, deleteTaxation, propertyTicker, showMessageDeleted]
  );

  const SwitchRenderer = useCallback(({ value }: RendererProps<boolean>) => {
    return (
      <SwitchWrapper>
        <SwitchComponent
          value={!!value}
          field={{
            className: "witadmin-bs3",
          }}
          caller=""
          entity={{}}
        />
      </SwitchWrapper>
    );
  }, []);

  const FeeTableRowActions = useCallback(
    ({ row, rest, onReloadList }: DefaultTableRowActionsProps<CustomTaxation>) => {
      return (
        <Table.Cell {...rest}>
          <StyledReactTooltip id="edit" effect="solid">
            <FormattedMessage {...customerMessages.edit} />
          </StyledReactTooltip>
          <StyledReactTooltip id="delete" effect="solid">
            <FormattedMessage {...customerMessages.delete} />
          </StyledReactTooltip>

          <Edit
            onGoToEdit={id => history.push(`${match.url}/edit/${id}`)}
            row={{ ...row, lCountry: undefined }}
          />
          <Delete rest={rest} onDelete={onDeleteFee} row={row} onReloadList={onReloadList} />
        </Table.Cell>
      );
    },
    [onDeleteFee, history, match.url]
  );

  const feeFormConfiguration: FormConfiguration<CustomTaxation> = useMemo(
    () => ({
      __fieldOrder: [
        "id",
        "active",
        "ticker",
        "translationsList",
        "typologyModule",
        "taxPreferenceModule",
        "validitySection",
        "thresholdModule",
        "*",
      ],
      __useProtoStyleArrays: true,
      restriction: {
        hidden: true,
      },
      createdAt: {
        hidden: true,
      },
      updatedAt: {
        hidden: true,
      },
      amount: {
        hidden: true,
      },
      typology: {
        hidden: true,
      },
      preference: {
        hidden: true,
      },
      threshold: {
        hidden: true,
      },
      type: {
        hidden: true,
      },
      restrictionsSection: {
        hidden: true,
      },
      maxNights: {
        hidden: true,
      },
      maxAmount: {
        hidden: true,
      },
      id: {
        hidden: true,
        default: () => uuidv4(),
        readonly: true,
        messages: {
          label: defineMessage({
            id: "taxation.id",
            defaultMessage: "id",
          }),
        },
      },
      active: {
        widget: SwitchGroup,
        messages: {
          label: defineMessage({
            id: "taxation.active",
            defaultMessage: "active",
          }),
        },
      },
      ticker: {
        readonly: isNew => !isNew,
        messages: {
          label: defineMessage({
            id: "taxation.ticker",
            defaultMessage: "ticker",
          }),
        },
      },
      translationsList: {
        ...translationFieldConfig(allowedLocales || []),
        children: {
          __fieldOrder: ["title", "*"],
          locale: {
            hidden: true,
          },
          title: {
            messages: {
              label: defineMessage({
                id: "taxation.title",
                defaultMessage: "title",
              }),
            },
          },
          description: {
            widget: HtmlEditorWidget,
            messages: {
              label: defineMessage({
                id: "taxation.description",
                defaultMessage: "Description",
              }),
            },
          },
          ...translationFieldConfig(allowedLocales || []).children,
        },
        messages: {
          label: defineMessage({ id: "taxation.translations", defaultMessage: "Translations" }),
        },
      },
      typologyModule: {
        fieldWidget: TaxationTypologyManagerFee,
        messages: {
          label: defineMessage({
            id: "taxation.typology",
            defaultMessage: "typology",
          }),
        },
      },
      taxPreferenceModule: {
        hidden: true,
        readonly: true,
      },
      validitySection: {
        fieldWidget: SectionUnderline,
        messages: {
          label: defineMessage({
            id: "taxation.validity",
            defaultMessage: "validity",
          }),
        },
      },
      thresholdModule: {
        fieldWidget: DateRangePicker,
      },
    }),
    [allowedLocales]
  );

  const feeColumns: TableColumns<CustomTaxation> = useMemo(
    () => ({
      active: {
        renderer: SwitchRenderer,
        width: 100,
      },
      ticker: {
        renderer: TableRenderers.StringRenderer,
      },
      translationsList: {
        children: {
          title: {
            renderer: mapRenderer(StringRenderer, (v, intl) => {
              if (!v || !v.length) return undefined;
              const current = v.find(t => t.locale === getLanguageFromFullLocale(intl.locale));
              return current?.title || v[0].title;
            }),
          },
        },
      },
      typology: {
        renderer: TypologyRenderer,
      },
      amount: {
        renderer: TableRenderers.NumberRenderer,
      },
      threshold: {
        renderer: ThresholdRenderer,
      },
    }),
    [SwitchRenderer]
  );

  return (
    <SimpleCrud<CustomTaxation>
      schema={modifiedSchema}
      tableColumns={feeColumns}
      formConfiguration={feeFormConfiguration}
      messages={messagesFees}
      onGetAll={onGetAllFee}
      onGetOne={onGetOneFee}
      section={section}
      onUpdate={onUpdateFee}
      onCreate={onCreateFee}
      canCreate={actions.CreateMealPlan} //TODO change
      canUpdate={actions.ModifyMealPlan} //TODO change
      hideExportButton={true}
      forceLoad={latestEntity}
      rowActions={props => <FeeTableRowActions {...props} />}
    />
  );
}

export default withAuthorizationVerifier(FeesCrudComponent);
